import React, { useEffect, useMemo, useRef } from 'react'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { fetchDlpMainRecommendations } from 'actions/RecommendationActions'
import { lereDlpMainListKey } from 'lib/customer/recommendationUtils'
import { getListName } from 'analytics/enhanced-ecommerce'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import OfferList, { OfferListV2Item } from 'components/OfferList/OfferList'
import OfferListHeading from 'components/OfferList/OfferListHeading'
import { EmptyArray } from 'lib/array/arrayUtils'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'

interface OwnProps {
  lereLePlaceId: string;
}

interface Props extends OwnProps {
  extras?: Array<OfferListV2Item>;
  analyticPageSource: string;
  lereLoadingState: App.ApiResponseState;
  lereVersion?: string;
  location?: App.Place;
  lereOfferIds?: Array<string>;
}

function LocationLereOfferList(props: Props) {
  const {
    extras,
    analyticPageSource,
    lereLoadingState,
    lereLePlaceId,
    lereVersion,
    lereOfferIds = EmptyArray,
  } = props

  const dispatch = useAppDispatch()
  const fetchDispatched = useRef(false)

  useEffect(() => {
    if (fetchDispatched.current) {
      return
    }
    if (!lereOfferIds.length) {
      fetchDispatched.current = true
      dispatch(fetchDlpMainRecommendations(lereLePlaceId))
    }
  }, [dispatch, lereOfferIds, lereLePlaceId])

  const tracking = useOfferListTracking(
    getListName('LERE', analyticPageSource, 'Location Seeded Main Results'),
    {
      ga: {
        category: 'LERE',
        label: 'Location Seeded Main Results',
      },
      source: lereVersion ?? undefined,
    },
  )

  const lereOfferList = useMemo((): App.OfferList => {
    return {
      error: undefined,
      fetching: lereLoadingState === 'loading',
      offerIds: lereOfferIds,
      key: 'recommendationOffers',
    }
  }, [lereLoadingState, lereOfferIds])

  if (!lereOfferList.fetching && !lereOfferList.offerIds.length) {
    return <OfferListHeading
      title="We are currently sold out of our escapes!"
      description="Our travel experts are working hard on curating new offers. In the meantime check out our available offers in other exciting destinations."
    />
  }

  return <OfferListEventsProvider tracking={tracking}>
    <OfferList
      interstitials={extras}
      offerlistOverride={lereOfferList}
    />
  </OfferListEventsProvider>
}

function mapStateToProps(state: App.State, ownProps: OwnProps) {
  const key = lereDlpMainListKey(ownProps.lereLePlaceId)
  const offersState = state.recommendations.locationSeededRecommendations[key]
  return {
    lereLoadingState: offersState?.state,
    lereOfferIds: offersState?.offerIds,
    lereVersion: state.recommendations.lereVersion,
  }
}

export default connect(mapStateToProps)(LocationLereOfferList)
